import React, { useState, useEffect } from "react";
import { BiDownload, BiFilter, BiRefresh, BiGift } from "react-icons/bi";
import { useGiftcardReport } from "../../../hooks";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatDisplayDate,
  translateStatus,
  getStatusColor,
} from "../../../utils/formatters";
import {
  ReportFilters,
  ReportTabs,
  KPICard,
  DataTable,
  ExportModal,
} from "./shared";

const GiftcardsReport = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showCardDetails, setShowCardDetails] = useState(null);
  const [filters, setFilters] = useState({
    period: "month",
    startDate: null,
    endDate: null,
    status: null,
  });

  const {
    loading,
    error,
    summary,
    getSummary,
    getList,
    getUsage,
    calculateUsageStats,
  } = useGiftcardReport();

  const [giftcardSummary, setGiftcardSummary] = useState(null);
  const [giftcards, setGiftcards] = useState({ data: [], pagination: {} });
  const [usage, setUsage] = useState({ data: [], pagination: {} });
  const [selectedCard, setSelectedCard] = useState(null);

  useEffect(() => {
    loadTabData();
  }, [activeTab, filters]);

  const loadTabData = async () => {
    try {
      switch (activeTab) {
        case "summary":
          const summaryData = await getSummary(filters);
          setGiftcardSummary(summaryData);
          break;
        case "list":
          const listResult = await getList({ ...filters, page: 1 });
          setGiftcards(listResult);
          break;
        case "usage":
          const usageResult = await getUsage({ ...filters, page: 1 });
          setUsage(usageResult);
          break;
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    }
  };

  const handlePageChange = async (page) => {
    try {
      switch (activeTab) {
        case "list":
          const listResult = await getList({ ...filters, page });
          setGiftcards(listResult);
          break;
        case "usage":
          const usageResult = await getUsage({ ...filters, page });
          setUsage(usageResult);
          break;
      }
    } catch (error) {
      console.error("Erreur pagination:", error);
    }
  };

  const handleRowClick = (card) => {
    setSelectedCard(card);
    setShowCardDetails(true);
  };

  const handleExport = async (format, options) => {
    // Implémenter l'export
    console.log("Export:", format, options);
  };

  const tabs = [
    { id: "summary", label: "Résumé" },
    { id: "list", label: "Cartes cadeaux", count: giftcards.pagination?.total },
    { id: "usage", label: "Utilisations", count: usage.pagination?.total },
  ];

  const giftcardColumns = [
    {
      key: "code",
      label: "Code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    { key: "serviceName", label: "Service" },
    {
      key: "totalValue",
      label: "Valeur totale",
      render: (row) => formatCurrency(row.totalValue),
    },
    {
      key: "quantity",
      label: "Quantité",
      render: (row) => formatNumber(row.quantity),
    },
    {
      key: "value",
      label: "Valeur unitaire",
      render: (row) => formatCurrency(row.value),
    },
    {
      key: "status",
      label: "Statut",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${getStatusColor(row.status, "giftcard")}`}
        >
          {translateStatus(row.status, "giftcard")}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Date création",
      render: (row) => formatDisplayDate(row.createdAt),
    },
  ];

  const usageColumns = [
    {
      key: "cardCode",
      label: "Carte",
      render: (row) => <span className="font-mono">{row.cardCode}</span>,
    },
    { key: "clientName", label: "Client" },
    { key: "serviceName", label: "Service utilisé" },
    {
      key: "usedFor",
      label: "Rendez-vous",
      render: (row) => <span className="font-mono">{row.usedFor}</span>,
    },
    {
      key: "value",
      label: "Montant",
      render: (row) => formatCurrency(row.value),
    },
    {
      key: "usedAt",
      label: "Date utilisation",
      render: (row) => formatDisplayDate(row.usedAt, "dd/MM/yyyy HH:mm"),
    },
    { key: "employeeName", label: "Employé" },
  ];

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Une erreur est survenue : {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-poppins font-semibold text-[20px] text-title">
          Rapport des cartes cadeaux
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiFilter size={18} />
            Filtres
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
          >
            <BiDownload size={18} />
            Exporter
          </button>
          <button
            onClick={loadTabData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Onglets */}
      <ReportTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
        </div>
      ) : (
        <>
          {activeTab === "summary" && giftcardSummary && (
            <div className="space-y-6">
              {/* KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="Total cartes"
                  value={formatNumber(giftcardSummary.overview.totalCards)}
                  icon="🎁"
                  color="pink"
                />
                <KPICard
                  title="Valeur totale"
                  value={formatCurrency(giftcardSummary.overview.totalValue)}
                  icon="💰"
                  color="purple"
                />
                <KPICard
                  title="Valeur utilisée"
                  value={formatCurrency(giftcardSummary.overview.usedValue)}
                  icon="✅"
                  color="green"
                />
                <KPICard
                  title="Taux d'utilisation"
                  value={formatPercentage(giftcardSummary.overview.usageRate)}
                  icon="📊"
                  color="blue"
                />
              </div>

              {/* Répartition par statut */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-poppins font-semibold text-[16px] mb-4">
                  Répartition par statut
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(giftcardSummary.byStatus).map(
                    ([status, data]) => (
                      <div key={status} className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500 mb-1">
                          {translateStatus(status, "giftcard")}
                        </p>
                        <p className="text-2xl font-bold">
                          {formatNumber(data.count)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(data.value)}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatPercentage(data.percentage)}
                        </p>
                      </div>
                    ),
                  )}
                </div>
              </div>

              {/* Évolution mensuelle */}
              {giftcardSummary.byMonth && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-poppins font-semibold text-[16px] mb-4">
                    Évolution mensuelle
                  </h3>
                  <div className="space-y-3">
                    {giftcardSummary.byMonth.map((month) => (
                      <div
                        key={month.month}
                        className="flex items-center gap-4"
                      >
                        <span className="w-24 font-nunito text-[14px]">
                          {month.month}
                        </span>
                        <div className="flex-1 flex items-center gap-4">
                          <div className="flex-1">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-green-500 rounded-full"
                                style={{
                                  width: `${(month.issued / Math.max(...giftcardSummary.byMonth.map((m) => m.issued))) * 100}%`,
                                }}
                              />
                            </div>
                          </div>
                          <span className="w-20 text-right">
                            {formatNumber(month.issued)}
                          </span>
                          <span className="w-24 text-right text-green-600">
                            {formatCurrency(month.value)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top services */}
              {giftcardSummary.byService && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="font-poppins font-semibold text-[16px] mb-4">
                    Cartes par service
                  </h3>
                  <div className="space-y-3">
                    {giftcardSummary.byService.slice(0, 5).map((service) => (
                      <div
                        key={service.serviceId}
                        className="flex items-center justify-between"
                      >
                        <span className="font-nunito text-[14px]">
                          {service.serviceName}
                        </span>
                        <div className="flex items-center gap-4">
                          <span className="font-nunito text-[14px]">
                            {formatNumber(service.count)} cartes
                          </span>
                          <span className="font-nunito text-[14px]">
                            {formatCurrency(service.value)}
                          </span>
                          <span className="font-nunito text-[12px] text-gray-500">
                            {formatNumber(service.usedCount)} utilisées
                          </span>
                          <span className="font-nunito text-[12px] text-green-600">
                            {formatPercentage(service.usageRate)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Période */}
              <div className="text-sm text-gray-500 text-right">
                Période du {formatDisplayDate(giftcardSummary.period.start)} au{" "}
                {formatDisplayDate(giftcardSummary.period.end)}
              </div>
            </div>
          )}

          {activeTab === "list" && (
            <DataTable
              columns={giftcardColumns}
              data={giftcards.data}
              pagination={giftcards.pagination}
              onPageChange={handlePageChange}
              onRowClick={handleRowClick}
            />
          )}

          {activeTab === "usage" && (
            <DataTable
              columns={usageColumns}
              data={usage.data}
              pagination={usage.pagination}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Modal détails carte */}
      {showCardDetails && selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="font-poppins font-semibold text-[18px]">
                Détails de la carte {selectedCard.code}
              </h3>
              <button
                onClick={() => setShowCardDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Infos générales */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Service</p>
                    <p className="font-medium">{selectedCard.serviceName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Statut</p>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${getStatusColor(selectedCard.status, "giftcard")}`}
                    >
                      {translateStatus(selectedCard.status, "giftcard")}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Valeur unitaire</p>
                    <p className="font-medium">
                      {formatCurrency(selectedCard.value)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Quantité</p>
                    <p className="font-medium">
                      {formatNumber(selectedCard.quantity)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Valeur totale</p>
                    <p className="font-medium text-green-600">
                      {formatCurrency(selectedCard.totalValue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date création</p>
                    <p className="font-medium">
                      {formatDisplayDate(selectedCard.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Cartes individuelles */}
              {selectedCard.individualCards &&
                selectedCard.individualCards.length > 0 && (
                  <div>
                    <h4 className="font-poppins font-semibold text-[16px] mb-3">
                      Codes individuels
                    </h4>
                    <div className="space-y-2">
                      {selectedCard.individualCards.map((card, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-3 bg-gray-50 rounded"
                        >
                          <span className="font-mono">{card.code}</span>
                          {card.used ? (
                            <div className="flex items-center gap-3">
                              <span className="text-sm text-gray-600">
                                Utilisé le {formatDisplayDate(card.usedAt)}
                              </span>
                              <span className="text-sm text-gray-600">
                                par {card.usedBy}
                              </span>
                              {card.appointmentCode && (
                                <span className="text-xs font-mono bg-gray-200 px-2 py-1 rounded">
                                  {card.appointmentCode}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-green-600">
                              Non utilisé
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export */}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default GiftcardsReport;
